import { QueryClient } from "@tanstack/react-query";

import { ApiError } from "./client";

/**
 * Einstellungen von TanStack Query, abgestimmt auf schwankende Mobilfunkverbindung.
 *
 * Die Voreinstellungen der Bibliothek sind für Bürobetrieb gedacht. Zwei
 * Abweichungen sind hier wesentlich:
 *
 *  - `retry` prüft die Art des Fehlers. Ein 403 wird durch Wiederholen nie zu
 *    einem 200; drei Versuche kosten dann nur Zeit und Akku.
 *  - `staleTime` von einer Minute. Objekt- und Raumdaten ändern sich selten;
 *    ohne diese Frist lädt jeder Wechsel zwischen zwei Ansichten neu, was im
 *    Keller jedes Mal in die Zeitüberschreitung läuft.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 60_000,
        gcTime: 24 * 60 * 60 * 1000,
        // Kein erneutes Laden beim Fensterwechsel: Auf dem Telefon wechselt der
        // Fokus bei jeder Benachrichtigung, das wäre eine Abfrage je Hinweiston.
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
        retry: (failureCount, error) => {
          if (error instanceof ApiError) {
            if (error.isOffline) return failureCount < 2;
            // Anmeldung und Berechtigung klärt der Client, nicht die Wiederholung.
            if (error.status === 401 || error.status === 403) return false;
            if (error.status >= 400 && error.status < 500) return false;
          }
          return failureCount < 2;
        },
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 15_000),
      },
      mutations: {
        // Schreibende Aufrufe werden nie automatisch wiederholt: Ohne
        // Idempotenzschlüssel könnte daraus ein zweiter Vorgang entstehen. Wer
        // Wiederholung braucht, nimmt die Warteschlange (src/lib/queue.ts).
        retry: false,
        /*
         * **Ohne Netz trotzdem ausführen.**
         *
         * TanStack Query hält eine Änderung von sich aus an, sobald
         * `navigator.onLine` falsch ist: Die Ausführungsfunktion wird gar nicht
         * erst aufgerufen, der Vorgang bleibt auf „läuft" stehen und wird erst
         * bei Rückkehr des Netzes nachgeholt. Das klingt hilfreich und ist hier
         * zweimal falsch.
         *
         * Erstens ist dieser Wartekorb **flüchtig**: Er liegt im Arbeitsspeicher
         * der Seite. Wer die Anwendung wegwischt oder das Telefon neu startet,
         * verliert alles darin — genau der Abbruch, gegen den die Warteschlange
         * in `src/lib/queue.ts` gebaut ist. Sie schreibt nach IndexedDB und
         * übersteht deshalb einen Neustart.
         *
         * Zweitens verdeckt das Anhalten die Lage: Der Nutzer sieht einen
         * Ladekringel, der nie endet, und keine Aussage darüber, ob seine
         * Eingabe irgendwo liegt. Bei einem Statuswechsel oder einer
         * Rechnungsfreigabe — die bewusst **nicht** eingereiht werden — ist die
         * ehrliche Fehlermeldung „Keine Verbindung zum Server" die richtige
         * Antwort, nicht ein stiller Aufschub.
         *
         * Mit `always` läuft die Ausführungsfunktion also immer. Die
         * eingereihten Handlungen fangen den Verbindungsabbruch selbst ab
         * (`sendeOderReihEin`), alle übrigen scheitern sichtbar.
         */
        networkMode: "always",
      },
    },
  });
}

/** Abfrageschlüssel an einer Stelle, damit Ungültigmachen zuverlässig trifft. */
export const queryKeys = {
  me: ["auth", "me"] as const,
  properties: (search?: string) => ["properties", { search: search ?? "" }] as const,
  property: (id: string) => ["properties", id] as const,
  propertyTree: (id: string) => ["properties", id, "tree"] as const,
  rooms: (propertyId: string) => ["rooms", { propertyId }] as const,
};
